/**
 * @file useSelectEx.js
 * @description フォーム選択フィールドを簡単に生成するためのカスタムフック
 *
 * @example
 * // 基本的な使用例
 * const [form, formProps] = useFormEx();
 * const { select_dbms } = useSelectEx(form, formProps);
 *
 * // コンポーネント内での使用
 * return (
 *   <form>
 *     {select_dbms({
 *       name: "dbms",
 *       label: "DBMS",
 *       required: true
 *     })}
 *   </form>
 * );
 *
 * @author MetroJS Team
 * @version 1.0.0
 * @since 2024-03-01
 */

"use client";
import Consts from "@common/config/consts";
import { convertJapaneseYearForSelect, makeSharyoNenshikiNashiOptions } from "../../utils/sharyoNenshikiOptions.js";

import logjs from "@metrojs/logjs";
const log = new logjs("useSelectEx");

// ベーススタイル定義 - 選択フィールドのデフォルトスタイル
const baseStyle = {
  width: "150px",
  padding: "3px 5px",
  border: "1px solid #ccc",
  borderRadius: "3px",
};
// 空の選択肢（デフォルト表示用）
const NOT_SELECT_OPTIONS = [{ key: "not_select", value: "", label: "▼選択してください" }];
const EMPTY_OPTIONS = [{ key: "empty", value: "-1", label: "" }];

/**
 * フォーム選択フィールドを簡単に生成するためのカスタムフック
 * @param {Object} form - フォームオブジェクト
 * @param {Object} formProps - フォームプロパティ
 * @param {Object} state - アプリケーション状態
 * @param {Object} actions - アクション関数
 * @returns {Object} 各種選択フィールド生成関数のオブジェクト
 */
const useSelectEx = (form = {}, formProps = {}, state = {}, actions = {}) => {
  const is_standard = true; // 標準の選択フィールドを使用するかどうか

  const select_ReEstimateEeason = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_ReEstimateEeason params:`, params);
    }

    const NO_SELECT = { key: "-1", value: "", label: "▼選択してください" };
    const options = [NO_SELECT, ...Consts.SELECTION.RE_ESTIMATE_REASON];
    return formProps.select("Reason", { ...params, options, is_standard, style: { ...baseStyle, width: "270px", ...params.style } }, is_debug);
  };

  /**
   * 時間選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 時間選択フィールド
   */
  const select_hour = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_hour params:`, params);
    }
    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    const options = hours.map((hour) => ({
      key: hour,
      value: hour,
      label: Number(hour),
    }));

    return formProps.select(
      "hour",
      {
        is_standard,
        style: { ...baseStyle, width: "5em", ...params.style },
        options: options,
        ...params,
      },
      is_debug
    );
  };

  /**
   * 発送先選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 発送先選択フィールド
   */
  const select_hassosaki_shipping = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_hassosaki_shipping params:`, params);
    }

    const NOT_SELECT = [
      {
        key: "-1",
        value: "",
        label: "▼登録されている発送先を選択する",
      },
    ];

    // params.optionsが配列でない場合は空配列にフォールバック
    const options = Array.isArray(params?.options) ? params.options : [];

    // オプションにkeyプロパティを確実に設定
    const mappedOptions = options.map((item, index) => ({
      key: item.key || `option-${index}`, // keyが存在しない場合はインデックスで生成
      value: item.value,
      label: item.label,
    }));

    params.options = NOT_SELECT.concat(mappedOptions);

    return formProps.select(
      "hassosaki_shipping",
      {
        is_standard,
        style: { ...baseStyle, width: "270px", ...params.style },
        options: params.options,
        ...params,
      },
      is_debug
    );
  };

  /**
   * 都道府県選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 都道府県選択フィールド
   */
  const select_area = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_area params:`, params);
    }

    // 初期データから都道府県のオプションを生成（null/undefinedチェックを追加）
    const area = state.initData?.area;
    if (!area || !Array.isArray(area)) {
      // areaデータがない場合は空のオプションを返す
      return formProps.select("area", { ...params, options: NOT_SELECT_OPTIONS, is_standard, style: { ...baseStyle, ...params.style } }, is_debug);
    }

    const options = area
      .filter((item) => item && item.AreaName) // null/undefined/空の要素をフィルタ
      .map((item, index) => ({
        key: `area-${item.AreaName}-${index}`, // ユニークなキーを生成
        value: item.AreaName,
        label: item.AreaName,
      }));

    return formProps.select("area", { ...params, options: NOT_SELECT_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * 車種分類選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 車種分類選択フィールド
   */
  const select_sbunrui = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_sbunrui params:`, params);
    }

    // 初期データから車種分類のオプションを生成（null/undefinedチェックを追加）
    const sbunrui = state.initData?.sbunrui;
    if (!sbunrui || !Array.isArray(sbunrui)) {
      // sbunruiデータがない場合は空のオプションを返す
      return formProps.select("sbunrui", {
        ...params,
        id: "sbunrui",
        options: NOT_SELECT_OPTIONS,
        is_standard,
        style: { ...baseStyle, width: "150px", ...params.style },
      });
    }

    const options = sbunrui
      .filter((item) => item && item.BunruiCD && item.BunruiName) // null/undefined/空の要素をフィルタ
      .map((item, index) => ({
        key: `sbunrui-${item.BunruiCD}-${index}`, // ユニークなキーを生成
        value: item.BunruiCD,
        label: item.BunruiName,
      }));

    return formProps.select(
      "sbunrui",
      {
        ...params,
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        style: { ...baseStyle, width: "150px", ...params.style },
      },
      is_debug
    );
  };

  /**
   * エンジン区分選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} エンジン区分選択フィールド
   */
  const select_Sharyo_Ekubun = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Sharyo_Ekubun params:`, params);
    }

    // 定数からエンジン区分のオプションを生成
    const options = Consts.SELECTION.E_KUBUN.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));

    return formProps.select("Sharyo_Ekubun", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  const select_Hasso = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Hasso params:`, params);
    }

    const options = Consts.SELECTION.HASSO_STATUS.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));
    return formProps.select("Hasso", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  const select_Haiso_kubun = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Haiso_kubun params:`, params);
    }

    const options = Consts.SELECTION.HAISO_KUBUN.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));
    return formProps.select("Haiso_kubun", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * トランスミッション選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} トランスミッション選択フィールド
   */
  const select_Sharyo_TransMission = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Sharyo_TransMission params:`, params);
    }

    // 定数からトランスミッションのオプションを生成
    const options = Consts.SELECTION.TRANS_MISSION.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));

    return formProps.select("Sharyo_TransMission", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * 駆動方式選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 駆動方式選択フィールド
   */
  const select_Sharyo_KudouHoushiki = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Sharyo_KudouHoushiki params:`, params);
    }

    // 定数から駆動方式のオプションを生成
    const options = Consts.SELECTION.KUDOU_HOUSHIKI.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));

    return formProps.select("Sharyo_KudouHoushiki", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * 西暦を和暦に変換する関数
   * @param {number} year - 西暦年
   * @returns {string} 和暦表記
   */
  /**
   * 年式選択フィールドを生成 年式なし付き
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 年式選択フィールド
   */
  const select_Sharyo_Nenshiki_Nashi = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Sharyo_Nenshiki_Nashi params:`, params);
    }

    const options = makeSharyoNenshikiNashiOptions(params);
    return formProps.select("Sharyo_Nenshiki", { ...params, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * 年式選択のオプションを生成
   * @param {Object} params - パラメータ（nashi_flg, includeNone等）
   * @returns {Array} 年式オプションの配列
   */
  const make_nenshiki_options = (params = {}) => {
    const options = [];
    let nen = new Date().getFullYear(); // 現在年から開始

    // 1993年まで遡って年式オプションを生成
    for (;;) {
      const wareki = convertJapaneseYearForSelect(nen);
      let label = `${wareki}(${nen}年)`;
      if (nen === 1993) {
        label += "以前";
      }
      options.push({ key: String(nen), value: String(nen), label });
      nen -= 1;
      if (nen < 1993) {
        break;
      }
    }

    // 年式なしオプションを追加する場合
    if (params.nashi_flg === 1 || params.includeNone) {
      options.push({ key: "年式なし", value: "年式なし", label: "年式なし" });
    }
    return options;
  };

  /**
   * 年式選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 年式選択フィールド
   */
  const select_Sharyo_Nenshiki = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Sharyo_Nenshiki params:`, params);
    }

    const options = make_nenshiki_options(params);
    return formProps.select("Sharyo_Nenshiki", { options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style }, ...params }, is_debug);
  };

  /**
   * メーカー選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} メーカー選択フィールド
   */
  const select_MakerCD = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_MakerCD params:`, params);
    }

    // 初期データからメーカーのオプションを生成（null/undefinedチェックを追加）
    const makers = state.initData?.makers;
    if (!makers || !Array.isArray(makers)) {
      // makersデータがない場合は空のオプションを返す
      return formProps.select(
        "MakerCD",
        {
          ...params,
          options: NOT_SELECT_OPTIONS,
          is_standard,
        },
        is_debug
      );
    }

    const options = makers
      .filter((item) => item && item.MakerCD && item.MakerName) // null/undefined/空の要素をフィルタ
      .map((item) => ({
        key: item.MakerCD,
        value: item.MakerCD,
        label: item.MakerName,
      }));

    return formProps.select(
      "MakerCD",
      {
        ...params,
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        style: { ...baseStyle, width: "170px", ...params.style },
      },
      is_debug
    );
  };

  /**
   * 車メーカー選択フィールドを生成
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 車メーカー選択フィールド
   */
  const select_Maker = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Maker params:`, params);
    }

    const options = Consts.SELECTION.CAR_MAKER.map((item) => ({
      key: item.key,
      value: item.key,
      label: item.value,
    }));

    return formProps.select(
      "Maker",
      {
        ...params,
        label: "メーカー",
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        style: { ...baseStyle, width: "150px", ...params.style },
      },
      is_debug
    );
  };

  /**
   * 会員種別セレクト（業種検索用）。params.syubetsuSelect（key=>label のオブジェクト）を options として formProps.select で描画する。
   * @param {Object} params - 追加パラメータ。syubetsuSelect: { [key]: label } でオプション元を渡す。style 等も指定可。
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 会員種別選択フィールド
   */
  const select_CorpKind = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_CorpKind params:`, params);
    }
    const syubetsuSelect = params.syubetsuSelect != null ? params.syubetsuSelect : {};
    const options = [
      { key: "corp-empty", value: "", label: "" },
      ...Object.entries(syubetsuSelect).map(([k, v]) => ({
        key: String(k),
        value: String(k),
        label: String(v),
      })),
    ];
    return formProps.select("CorpKind", {
      ...params,
      options,
      is_standard,
      style: { ...baseStyle, width: "150px", ...params.style },
    }, is_debug);
  };

  /**
   * 種別選択フィールドを生成（お知らせ等：全体 / グループ / 業種）
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 種別選択フィールド
   */
  const select_Kind = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_Kind params:`, params);
    }

    const KIND_OPTIONS = [
      { key: "kind-empty", value: "", label: "" },
      { key: "kind-0", value: "0", label: "全体" },
      { key: "kind-1", value: "1", label: "グループ" },
      { key: "kind-2", value: "2", label: "業種" },
    ];

    return formProps.select(
      "Kind",
      {
        ...params,
        options: KIND_OPTIONS,
        is_standard,
        style: { ...baseStyle, width: "150px", ...params.style },
      },
      is_debug
    );
  };

  /**
   *
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} 都道府県選択フィールド
   */
  const select_PartsKindID = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_PartsKindID params:`, params);
    }

    // 初期データから部品区分のオプションを生成
    const partskind = state.initData?.partskind;
    if (!partskind || !Array.isArray(partskind) || partskind.length === 0) {
      //return formProps.select("PartsKindID", { options: NOT_SELECT_OPTIONS, is_standard, style: { ...baseStyle, ...params.style }, ...params }, is_debug);
      return null;
    }

    const options = partskind
      .filter((item) => item && (item.PartsKindID ?? item.partsKindId) != null && (item.PartsKindName ?? item.partsKindName))
      .map((item, index) => {
        const id = item.PartsKindID ?? item.partsKindId;
        const name = item.PartsKindName ?? item.partsKindName;
        return {
          key: `partskind-${id}-${index}`,
          value: Number(id),
          label: name,
        };
      });

    //log.debug(`select_PartsKindID options:`, options);
    const value = params?.value === null || params?.value === undefined ? -1 : Number(params.value);

    return formProps.select("PartsKindID", { ...params, value, options: EMPTY_OPTIONS.concat(options), is_standard, style: { ...baseStyle, width: "150px", ...params.style } }, is_debug);
  };

  /**
   * 支店コード選択フィールドを生成（単一選択）
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element|null} 支店コード選択フィールド
   
  const select_shiten_code = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_shiten_code params:`, params);
      log.debug(`state:`, state);
    }

    const shiten_info = state.initData?.shiten_info;
    if (!shiten_info) {
      return null; // 支店情報がない場合はnullを返す
    }

    // 支店情報からオプションを生成
    const options = shiten_info.map((item) => ({
      key: item.shiten_code,
      value: item.shiten_code,
      label: item.shiten_code + ":" + item.shiten_name,
    }));

    return formProps.select(
      "shiten_code",
      {
        label: "支店",
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        style: baseStyle,
        ...params,
      },
      is_debug
    );
  };
  */

  /**
   * 支店コード選択フィールドを生成（複数選択）
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element|null} 支店コード複数選択フィールド
   */
  /*
  const selectMulti_shiten_code = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_shiten_code params:`, params);
    }

    const shiten_info = state.initData?.shiten_info;
    if (!shiten_info) {
      return null; // 支店情報がない場合はnullを返す
    }

    // 支店情報からオプションを生成（複数選択用）
    const options = shiten_info.map((item) => ({
      key: item.shiten_code,
      value: item.shiten_code,
      label: item.shiten_name,
    }));

    return formProps.selectMulti(
      "shiten_code",
      {
        label: "支店",
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        ...params,
      },
      is_debug
    );
  };
  */

  /**
   * ユーザー権限選択フィールドを生成（基本版）
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} ユーザー権限選択フィールド
   */
  /*
  const select_auth = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_auth params:`, params);
    }

    // 基本の権限オプション
    const options = [
      { key: 7, value: 7, label: "管理者" },
      { key: 0, value: 0, label: "一般" },
    ];

    return formProps.select(
      "auth",
      {
        label: "ユーザー権限",
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        ...params,
      },
      is_debug
    );
  };

  /**
   * ユーザー権限選択フィールドを生成（全権限版）
   * @param {Object} params - パラメータ
   * @param {boolean} is_debug - デバッグモード
   * @returns {JSX.Element} ユーザー権限選択フィールド（全権限）
   */
  /*
  const select_auth_all = (params = {}, is_debug = false) => {
    if (is_debug) {
      log.debug(`select_auth params:`, params);
    }

    // 全権限のオプション
    const options = [
      { key: 9, value: 9, label: "開発者" },
      { key: 8, value: 8, label: "システム管理者" },
      { key: 7, value: 7, label: "管理者" },
      { key: 0, value: 0, label: "一般" },
    ];

    return formProps.select(
      "auth",
      {
        label: "ユーザー権限",
        options: NOT_SELECT_OPTIONS.concat(options),
        is_standard,
        ...params,
      },
      is_debug
    );
  };
  */
  // エクスポートする関数の一覧
  return {
    select_hour,
    select_hassosaki_shipping, // 発送先選択
    select_Hasso, // 発送選択
    select_area, // 都道府県選択
    select_MakerCD, // メーカー選択
    select_Maker, // 車メーカー選択
    select_Kind, // 種別選択（全体/グループ/業種）
    select_CorpKind, // 会員種別選択（業種検索用）
    select_PartsKindID, // 部品区分選択
    select_Sharyo_Nenshiki_Nashi, // 年式選択 年式なし付き
    select_Sharyo_Nenshiki, // 年式選択
    select_Sharyo_KudouHoushiki, // 駆動方式選択
    select_Sharyo_TransMission, // トランスミッション選択
    select_Sharyo_Ekubun, // エンジン区分選択
    select_sbunrui, // 車種分類選択
    select_ReEstimateEeason, // 再見積理由選択
    select_Haiso_kubun, // 発送区分選択
  };
};

export default useSelectEx;
